import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import { ErrorDialogComponent } from 'src/app/dialog/error-dialog/error-dialog.component';
import { Environment } from 'src/app/models/environment';
import { Team } from 'src/app/oncall/models/team';
import { EnvironmentService } from 'src/app/services/environment.service';
import { OnCallService } from 'src/app/oncall/services/oncall.service';
import { ConfirmationDialogComponent } from '../../components/confirmation-dialog/confirmation-dialog.component';
import { CreateTeamDialogComponent } from '../dialog/create-team-dialog/create-team-dialog.component';
import { ToastService } from 'src/app/services/toast.service';
import { Router } from '@angular/router';
import { AlertOnCallDialogComponent } from '../dialog/alert-oncall-dialog/alert-oncall-dialog.component';
import { UserProfileService } from 'src/app/services/user-profile.service';
import { LoadingService } from 'src/app/services/loading.service';

@Component({
    selector: 'app-oncall-team',
    templateUrl: './team.component.html',
    styleUrls: ['./team.component.css', '../../home/home.component.css'],
    standalone: false
})
export class TeamComponent implements OnInit, OnDestroy {
  environment: Environment;
  pageEnabled: boolean;
  teams: Array<Team>;
  displayedColumns: string[] = ['name', 'members', 'escalationPolicies', 'processingWorkflows', 'onCallTeamMember',  'splunkOnCallIntegrated', 'action'];
  columns: any[] = [];
  dataSource!: MatTableDataSource<Team>;
  newTeamName?: string;
  isPlatform: boolean = false;
  currentUserEmail: string = '';
  currentUserName: string = '';
  editTeamUrl: string = '/oncall/teams/edit';
  private readonly _destroying$ = new Subject<void>();
  @ViewChild('teamsTable') teamsTable!: MatTable<any>;

  constructor(
    private readonly userProfileService: UserProfileService,
    private environmentService: EnvironmentService,
    private onCallService: OnCallService,
    private dialog: MatDialog,
    private readonly loadingService: LoadingService,
    @Inject(Router) private readonly router: Router,
    private readonly toast: ToastService
  ) {
    this.environment = this.environmentService.getEnvironment();
    this.pageEnabled = this.environment.featureToggles.onCallEnabled === 'true';
    this.teams = new Array<Team>();
  }

  ngOnInit(): void {
    this.dataSource = new MatTableDataSource<Team>([]);
    this.columns = this.getColumns();

    // Get user information from claims
    const claims = this.userProfileService.getActiveClaims();
    if (claims) {
      // Get user email and name using the centralized methods
      this.userProfileService.getUserEmail().subscribe(email => {
        this.currentUserEmail = email;
      });
      this.userProfileService.getUserName().subscribe(name => {
        this.currentUserName = name;
      });

      // Check if user is a platform team member
      this.userProfileService.isPlatformTeamMember().subscribe(isPlatformMember => {
        this.isPlatform = isPlatformMember;
      });
    }

    this.getTeams();
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  getTeams(): void {
    this.loadingService.show('teams');
    this.teams = new Array<Team>();
    this.onCallService.getTeams()
      .subscribe({
        next: (teams) => {
          this.teams = teams;
          this.dataSource.data = this.teams;
          this.loadingService.hide('teams');
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'Error fetching teams');
        },
      });
  }

  createTeam(teamName: string): void {
    this.loadingService.show('createTeam');
    let team: Team = {
      name: teamName,
      members: [],
      escalationPolicies: [],
      onCallTeamMember: {
        id: ''
      },
      processingWorkflows: [],
      supportHours: {
        days: [],
        startTime: '',
        endTime: '',
        timeZone: '',
        enabled: false
      },
      splunkOnCallIntegrated: false,
      splunkOnCallTeamName: '',
      splunkOnCallRoutingKey: ''
    }
    this.onCallService.createOrUpdateTeam(team)
      .subscribe({
        next: () => {
          this.teams.push(team);
          this.dataSource.data = this.teams;
          this.teamsTable.renderRows();
          this.loadingService.hide('createTeam');
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'Error creating team');
        }
      });
  }

  editTeam(team: Team): void {
    this.router.navigate([this.editTeamUrl, team.name.replace(/\s/g, '')]);
  }

  deleteTeam(context: any, team: Team): void {
    context.loadingService.show('deleteTeam');
    let name = team.name;
    context.onCallService.deleteTeam(name)
      .subscribe({
        next: () => {
          context.teams = context.getTeams();
          context.dataSource.data = context.teams;
          context.teamsTable.renderRows();
          context.loadingService.hide('deleteTeam');
          context.toast.success(`Deleted ${name}`);
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, 'Error deleting team');
        },
      });
  }

  sendAlertToOnCall(teamName: string, alertBody: any): void {
    this.loadingService.show('sendAlert');
    this.onCallService.sendAlertToOnCall(teamName, alertBody)
      .subscribe({
        next: () => {
            this.toast.success(`Alert successfully sent to ${teamName}`);
            this.loadingService.hide('sendAlert');
        },
        error: (error: HttpErrorResponse) => {
          this.handleError(error, `Error alerting on call member of ${teamName}`);
        }
      });
  }

  handleError(error: HttpErrorResponse, message: string): void {
    this.loadingService.hide('teams');
    this.loadingService.hide('createTeam');
    this.loadingService.hide('deleteTeam');
    this.loadingService.hide('sendAlert');
    this.openErrorDialog(`${message}: ${error.status} ${error.statusText}`);
  }

  confirmAction(
    message: string,
    confirmActionMethod: (context: any, team: Team) => void,
    team: Team
  ): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { confirmationText: message },
    });

    dialogRef.afterClosed().subscribe({
      next: (confirmed) => {
        if (confirmed) {
          confirmActionMethod(this, team);
        }
      },
      error: () => { },
    });
  }

  getColumns(): any[] {
    let columns =
      [
        {
          columnDef: 'name',
          header: 'Name',
          cell: (team: Team) => `${team.name}`
        },
        {
          columnDef: 'members',
          header: 'Members',
          cell: (team: Team) => team.splunkOnCallIntegrated ? 'N/A' : `${team.members.length}`
        },
        {
          columnDef: 'escalationPolicies',
          header: 'Escalation Policies',
          cell: (team: Team) => team.splunkOnCallIntegrated ? 'N/A' : `${team.escalationPolicies.length}`
        },
        {
          columnDef: 'processingWorkflows',
          header: 'Processing Workflows',
          cell: (team: Team) => `${team.processingWorkflows.length}`
        },
        {
          columnDef: 'onCallTeamMember',
          header: 'On Call',
          cell: (team: Team) => team.splunkOnCallIntegrated ? '' : `${team.onCallTeamMember.id}`
        },
        {
          columnDef: 'splunkOnCallIntegrated',
          header: 'Splunk On-Call Integrated',
          cell: (team: Team) => `${team.splunkOnCallIntegrated}`
        }
      ]
    return columns;
  }

  openCreateTeamDialog(): void {
    const dialogRef = this.dialog.open(CreateTeamDialogComponent, {
      data: { teamName: this.newTeamName }
    });

    dialogRef.afterClosed().subscribe({
      next: (teamName) => {
        if (teamName) {
          this.createTeam(teamName);
        }
      },
      error: () => { },
    });
  }

  openAlertOnCallDialog(teamToAlert: any): void {
    const dialogRef = this.dialog.open(AlertOnCallDialogComponent, {
      data: {
        teamToAlert: teamToAlert.name,
        userSendingAlert: this.currentUserName
      }
    });

    dialogRef.afterClosed().subscribe({
      next: (data) => {
        if (data) {
          this.sendAlertToOnCall(data.teamName, data.alertBody);
        }
      },
      error: () => { },
    });
  }

  openErrorDialog(errorMessage: string): void {
    this.dialog.open(ErrorDialogComponent, {
      data: { message: errorMessage }
    });
  }
}
